import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import Container from '@mui/material/Container';
import styled from 'styled-components';

import HeaderWithDivider from '@/operator/components/general/headers/HeaderWithDivider';
import ConnectionsListContent from '@/operator/components/modules/connections/ConnectionsListContent';
import useNotification from 'app/contexts/NotificationContext/hooks/useNotification';
import ConnectionsPageSkeleton from './ConnectionsPageSkeleton';
import { useGetConnectionsQuery } from 'generated/graphql';
import { useAppSelector } from 'hooks/index';
import { selectPartnerData } from 'store/partner/partnerSelector';

export const HeadlineContainer = styled.div`
    justify-content: space-between;
    display: grid;
    grid-template-columns: auto auto;
    align-items: center;
    margin-bottom: 30px;
`;

const ConnectionsPage = (): JSX.Element => {
    const { t } = useTranslation();
    const { showNotification } = useNotification();
    const [searchParams] = useSearchParams();
    const notificationTrigger = searchParams.get('notification');
    const { instanceId } = useAppSelector(selectPartnerData);

    const { data, loading } = useGetConnectionsQuery({
        variables: {
            getConnectionInput: {
                where: {
                    organizationId: instanceId,
                },
            },
        },
        fetchPolicy: 'network-only',
    });

    useEffect(() => {
        if (notificationTrigger === 'success') {
            showNotification({
                message: t('Notifications.ConnectionCompleted'),
                severity: 'success',
            });
        } else if (notificationTrigger === 'connectionDelete') {
            showNotification({
                message: t('Notifications.ConnectionDeleted'),
                severity: 'success',
            });
        }
    }, [notificationTrigger]);

    if (loading) {
        return <ConnectionsPageSkeleton />;
    }

    return (
        <Container sx={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <HeaderWithDivider
                header={`${t('Forms.Connections.Common.Connection')}`}
            />

            <ConnectionsListContent connections={data?.connections} />
        </Container>
    );
};

export default ConnectionsPage;
